import { AppServices, PlatformSource, UCenterServices } from 'network'
import { SvIds } from '../constants/static-table-sv-id'
import { useChangeLanguage } from '../hooks/use-change-language'
import { useExchangeInfoStore } from './use-exchange-info'
import { useStaticTableDataStore } from './use-static-table-data'
import { useUserInfoStore } from './use-user-info'

export const useBaseInfoStore = defineStore('baseInfo', () => {
  /** 环境初始化的一些信息，包括ws链接地址等 */
  const webInitInfo = ref<ApiResponse.WebInitInfo>()
  /** 币种汇率 */
  const otcCoinRate = ref<ApiResponse.OtcCoinRate>()

  const staticTableDataStore = useStaticTableDataStore()

  const userInfoStore = useUserInfoStore()

  const exchangeInfoStore = useExchangeInfoStore()

  /** 获取环境初始化的一些信息，包括ws链接地址等 */
  const fetchWebInitInfo = async () => {
    const res = await AppServices.getWebInitInfo()
    // res.data.testFlightUrl = 'https://testflight.apple.com/join/ymqcEZwj'
    webInitInfo.value = res.data
  }

  const fetchOtcCoinRate = async () => {
    const res = await UCenterServices.getOtcCoinRate()
    otcCoinRate.value = res.data
  }

  const currentOtcCoinRate = computed(() => {
    const currency = userInfoStore.currentToCurrency as keyof ApiResponse.CoinRate
    if (!otcCoinRate.value) return '1'
    return otcCoinRate.value[userInfoStore.currentFromCurrency]?.[currency] || '1'
  })

  const hasCurrency = (currency: keyof ApiResponse.OtcCoinRate) => {
    if (!otcCoinRate.value) return false
    return otcCoinRate.value[currency]
  }

  const fetchAll = async () => {
    await Promise.all([fetchWebInitInfo(), fetchOtcCoinRate()])
  }

  useChangeLanguage(fetchAll)

  const getContractRecommendCoinList = (length?: number) => {
    if (!staticTableDataStore.staticTableDataMap[SvIds.ContractStaticSymbol]) return []
    const coinList = Object.values(staticTableDataStore.staticTableDataMap[SvIds.ContractStaticSymbol]).filter((item) => item.isRecommend === 1)
    const list = coinList.sort((a, b) => {
      return a.orderBy - b.orderBy
    })
    return list.slice(0, length || list.length)
  }

  const getSpotRecommendCoinList = (length?: number) => {
    const usdtItemId = 10007
    if (!staticTableDataStore.staticTableDataMap[SvIds.StaticSymbol]) return []
    const coinList = Object.values(staticTableDataStore.staticTableDataMap[SvIds.StaticSymbol]).filter((item) => item.isRecommend === 1 && item.coinTo === usdtItemId)
    const list = coinList.sort((a, b) => {
      return a.orderBy - b.orderBy
    })
    return list.slice(0, length || list.length)
  }

  const getCoinInfoByItemId = (itemId: number) => {
    if (!staticTableDataStore.staticTableDataMap[SvIds.StaticItemV2]) return null
    const data = staticTableDataStore.staticTableDataMap[SvIds.StaticItemV2][itemId]
    if (!data) return null
    return data
  }

  /** 通过symbolId获取一个币对的静态配置数据 */
  const getCoinStaticItemDataBySymbolId = (symbolId: SymbolId) => {
    if (!staticTableDataStore.staticTableDataMap[SvIds.StaticSymbol]) return null
    const data = staticTableDataStore.staticTableDataMap[SvIds.StaticSymbol][symbolId]
    if (!data) return null
    return data
  }

  const getGeeTestConfig = () => {
    if (!staticTableDataStore.staticTableDataMap[SvIds.MainGeetestCaptchaConfig]) return null
    const data = staticTableDataStore.staticTableDataMap[SvIds.MainGeetestCaptchaConfig]
    if (!data) return null
    return data
  }

  const getContractCoinStaticItemDataBySymbolId = (symbolId: SymbolId) => {
    return exchangeInfoStore.getContractSymbolInfoBySymbolId(symbolId)
  }

  const getContractSymbolName = (symbolId: SymbolId) => {
    const data = exchangeInfoStore.getContractSymbolInfoBySymbolId(symbolId)
    if (!data) return ''
    return data.symbolName
  }

  const getContractCoinIconUrlBySymbolId = (symbolId: SymbolId) => {
    const data = exchangeInfoStore.getContractSymbolInfoBySymbolId(symbolId)
    if (!data) return ''
    const changeItemId = data.contractItemId
    const coinInfo = getCoinInfoByItemId(changeItemId)
    if (!coinInfo) return ''
    return coinInfo.itemDetail.coinUrl
  }

  const registerGeeTestConfig = computed(() => {
    const config = getGeeTestConfig()
    if (!config) return null
    return config.find((item) => item.platform === PlatformSource.Web && item.type === 1)
  })

  const noRegisterGeeTestConfig = computed(() => {
    const config = getGeeTestConfig()
    if (!config) return null
    return config.find((item) => item.platform === PlatformSource.Web && item.type !== 1)
  })

  return {
    webInitInfo,
    getContractRecommendCoinList,
    getCoinInfoByItemId,
    getCoinStaticItemDataBySymbolId,
    currentOtcCoinRate,
    getGeeTestConfig,
    getSpotRecommendCoinList,
    hasCurrency,
    getContractSymbolName,
    getContractCoinStaticItemDataBySymbolId,
    getContractCoinIconUrlBySymbolId,
    registerGeeTestConfig,
    noRegisterGeeTestConfig,
  }
})
